/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useQueryParams } from '@jira/software__router-utils/src/useQueryParams.tsx';
import type { Query } from '@atlassian/react-resource-router';
import {
	QUERY_PARAM_TEXT,
	QUERY_PARAM_EPIC,
	QUERY_PARAM_STATUS_CATEGORY,
	QUERY_PARAM_WARNINGS,
	QUERY_PARAM_ASSIGNEE,
	QUERY_PARAM_SORT_BY,
	QUERY_PARAM_SORT_ORDER,
} from '../filter-sorting-query-param/constants.tsx';
import {
	isVersionStatusCategory,
	type SortBy,
	type SortOrder,
	type VersionStatusCategory,
	type WarningsCategory,
} from '../filter-sorting-query-param/types.tsx';
import { parseQueryParams } from '../filter-sorting-query-param/utils.tsx';

type AccountId = string;

const accountIdToAriStr = (
	accountIds: (AccountId | 'UNASSIGNED')[] | undefined,
): (string | null)[] | null => {
	if (accountIds == null) {
		return null;
	}

	return accountIds.map((accountId) =>
		accountId === 'UNASSIGNED' ? null : `ari:cloud:identity::user/${accountId}`,
	);
};

export const toVersionIssuesGraphQLVariables = (
	queryParams: Query,
): {
	filters: {
		searchStr: string | null;
		epicIds: string[] | null;
		statusCategories: VersionStatusCategory[] | null;
		warningCategories: WarningsCategory[] | null;
		assigneeAccountIds: (string | null)[] | null;
	};
	sortBy: {
		sortByField: SortBy;
		order: SortOrder;
	};
} => {
	const parsedQueryParams = parseQueryParams(queryParams);
	return {
		filters: {
			searchStr: parsedQueryParams[QUERY_PARAM_TEXT] ?? null,
			epicIds: parsedQueryParams[QUERY_PARAM_EPIC] ?? null,
			statusCategories:
				parsedQueryParams[QUERY_PARAM_STATUS_CATEGORY] != null
					? parsedQueryParams[QUERY_PARAM_STATUS_CATEGORY].filter(isVersionStatusCategory)
					: null,
			warningCategories: parsedQueryParams[QUERY_PARAM_WARNINGS] ?? null,
			assigneeAccountIds: accountIdToAriStr(parsedQueryParams[QUERY_PARAM_ASSIGNEE]),
		},
		sortBy: {
			sortByField: parsedQueryParams[QUERY_PARAM_SORT_BY] ?? 'CREATED',
			order: parsedQueryParams[QUERY_PARAM_SORT_ORDER] ?? 'ASC',
		},
	};
};

export const useVersionIssuesGraphQLVariables = (): {
	filters: {
		searchStr: string | null;
		epicIds: string[] | null;
		statusCategories: VersionStatusCategory[] | null;
		warningCategories: WarningsCategory[] | null;
		assigneeAccountIds: (string | null)[] | null;
	};
	sortBy: {
		sortByField: SortBy;
		order: SortOrder;
	};
} => {
	const [queryParams] = useQueryParams(
		QUERY_PARAM_TEXT,
		QUERY_PARAM_EPIC,
		QUERY_PARAM_STATUS_CATEGORY,
		QUERY_PARAM_WARNINGS,
		QUERY_PARAM_ASSIGNEE,
		QUERY_PARAM_SORT_BY,
		QUERY_PARAM_SORT_ORDER,
	);
	return toVersionIssuesGraphQLVariables(queryParams);
};
